/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { isApiSuccess } from '../../constants/platformStatusCodes';
import { AUDIT_STATUS } from './static/auditsConstants';
import {
  addCampaignAuditFiles,
  archiveCampaignAudit,
  confirmCampaignAudit,
  createCampaignAudit,
  deleteCampaignAudit,
  deleteCampaignAuditSource,
  getCampaignAudit,
  getCampaignAuditReport,
  getCampaignAuditSummary,
  listCampaignAudits,
  renameCampaignAudit,
  retryCampaignAuditSources,
  runCampaignAudit,
  unarchiveCampaignAudit,
} from './campaignAuditsApi';
import {
  buildInfluencerProfileUrl,
  formatSpend,
  stripCommas,
  stripScheme,
} from './static/auditsFormat';
import { friendlyConfirmError } from './static/auditsErrors';
import { parseServerTime } from './auditsRealtime';

// Total steps in the "Run an Audit" wizard: 1 = form, 2 = confirm.
export const TOTAL_AUDIT_STEPS = 2;

// Friendly card date, e.g. "2026-09-22 09:49:56" | ISO → "Sep 22, 2026".
const formatCardDate = (value) => {
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const capitalize = (value) => {
  if (!value) return null;
  const str = String(value);
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// card.timeframe {start,end} → "Feb 05, 2026 - Mar 14, 2026".
const formatFlightTime = (timeframe) => {
  if (!timeframe) return null;
  const start = formatCardDate(timeframe.start);
  const end = formatCardDate(timeframe.end);
  if (start && end) return `${start} - ${end}`;
  return start || end || null;
};

// A 3-letter currency code at the start or end of a budget text ("usd 350,000", "350,000 eur").
const CURRENCY_CODE_RE = /^[a-z]{3}(?=[\s\d])|(?<=[\d\s])[a-z]{3}$/gi;

// card.budget {amount,currency} → "USD 250,000". An amount that arrives as text with its code
// inside ("usd 350,000") keeps its wording, with the code in capitals.
const formatBudget = (budget) => {
  if (!budget || budget.amount == null) return null;
  const amount = Number(budget.amount);
  const value = Number.isFinite(amount)
    ? amount.toLocaleString('en-US')
    : String(budget.amount)
        .trim()
        .replace(CURRENCY_CODE_RE, (code) => code.toUpperCase());
  return budget.currency ? `${String(budget.currency).toUpperCase()} ${value}` : `${value}`;
};

// A create defaults the audit `name` to the first uploaded filename, so a
// filename-looking `name` shouldn't win over the extracted subject name — but a
// human-entered name (e.g. from Rename) should. Prefer the subject name only while
// `name` still looks like an uploaded file.
const UPLOAD_NAME_RE = /\.(pdf|csv|txt|docx?|xlsx?|pptx?|html?|md|json)$/i;
const cardTitle = (name, subjectName) => {
  if (name && !UPLOAD_NAME_RE.test(name)) return name; // human name (rename) wins
  return subjectName || name || null; // else prefer the extracted subject
};

// Build the homepage card view-model from a list row / detail that carries the BE
// `card` (subject/objective/timeframe/budget/adherence) + `createdBy`. Title prefers
// a human-entered `name` (Rename), else the extracted subject name; adherence is
// null until a run finishes.
const buildCardView = (row) => {
  const card = row.card || {};
  const subject = card.subject || {};
  return {
    subjectName: cardTitle(row.name, subject.name),
    subjectUrl: subject.url || null,
    image: null,
    typeValue: subject.type || null,
    subjectType: capitalize(subject.type),
    flightTime: formatFlightTime(card.timeframe),
    budget: formatBudget(card.budget),
    objective: capitalize(card.objective),
    adherenceScore: card.adherence?.score ?? null,
    createdByName: row.createdBy?.name || null,
    dateLabel: formatCardDate(row.createdAt),
    sourceCount: row.sourceCount,
  };
};

const mapListRowToCard = (row) => ({
  id: row.id,
  name: row.name,
  status: row.status,
  isAnalyzable: row.isAnalyzable,
  sourceCount: row.sourceCount,
  campaigns: row.campaigns || [],
  createdAt: row.createdAt,
  // Anchor for the stuck-pending check (last BE change, else creation).
  pendingSince: parseServerTime(row.updatedAt || row.createdAt),
  card: buildCardView(row),
});

// A summary number → the whole-number digit string the confirm inputs hold ('' when
// missing). Rounded first: the inputs are digits-only, so "39666.67" would otherwise
// lose its decimal point and become 3966667.
const toDigits = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  return Number.isFinite(n) ? String(Math.round(n)) : stripCommas(String(value));
};

// ML influencer outcomes ({ impressions, views, clicks, websiteClicks, conversions,
// engagement }) → the FE row's four outcome inputs. `viewsKey` remembers whether the
// ML reported impressions or views, so the chip label and the confirm body match it.
const mapSummaryOutcomes = (outcomes = {}) => {
  const viewsKey = outcomes.views == null && outcomes.impressions != null ? 'impressions' : 'views';
  return {
    viewsKey,
    outcomes: {
      views: toDigits(outcomes.views ?? outcomes.impressions),
      clicks: toDigits(outcomes.clicks ?? outcomes.websiteClicks),
      conversions: toDigits(outcomes.conversions),
      engagements: toDigits(outcomes.engagement ?? outcomes.engagements),
    },
  };
};

// Map the live GET /{id}/summary payload → confirm-form values (inverse of
// mapConfirmToCorrections). The subject block carries the objective and summary;
// each influencer carries its outcomes. The ML's audience (demographics /
// primaryInterests) isn't reviewed on Step 2, so it's left as the ML extracted it.
const mapSummaryToConfirm = (summary = {}) => {
  const subject = summary.subject || {};
  const totals = summary.totals || {};
  const timeframe = summary.timeframe || {};
  const currency = String(totals.currency || summary.currency || 'usd').toLowerCase();
  const pick = (key) => subject[key] ?? summary[key];
  return {
    subjectCompany: subject.subjectName || '',
    // The URL input shows its own https:// prefix.
    productUrl: stripScheme(subject.subjectUrl || ''),
    subjectType: subject.subjectType || '',
    otherSubjectType: '',
    objective: pick('objective') || '',
    subjectSummary: pick('subjectSummary') || '',
    currency,
    totalSpend: formatSpend(toDigits(totals.totalSpend)),
    campaignStartDate: timeframe.start || '',
    campaignEndDate: timeframe.end || '',
    influencers: (summary.influencers || []).map((inf, index) => ({
      id: inf.id || `inf-${index}`,
      handle: inf.handle || '',
      platform: inf.platform || '',
      // ML often omits the URL; rebuild it from platform + handle so the confirm sends one.
      profileUrl:
        stripScheme(inf.url || inf.profileUrl || '') ||
        buildInfluencerProfileUrl(inf.platform, inf.handle),
      spendCurrency: String(inf.spendCurrency || currency).toLowerCase(),
      spend: formatSpend(toDigits(inf.spend)),
      posts: inf.postCount != null ? String(inf.postCount) : '',
      ...mapSummaryOutcomes(inf.outcomes || {}),
    })),
  };
};

// Step 1 starts the audit from uploaded files OR an existing campaign (contract v7
// `campaignId`) — mutually exclusive; the mode is simply `campaignId != null`.
const emptyRunAudit = () => ({
  uploadedDocuments: [],
  campaignId: null,
  campaignNotes: '',
});

/** True when the audit was started from an existing campaign (has `type: campaign` sources). */
export const isCampaignAudit = (audit) =>
  (audit?.sources || []).some((source) => source?.type === 'campaign');

/**
 * A source the read-failure recovery may DELETE: refused/unreadable, and not a
 * platform-data campaign source (`removable: false` → the BE answers 422).
 */
export const isRemovableSource = (source) =>
  ['failed', 'unreadable'].includes(source?.status) && source?.removable !== false;

const emptyConfirm = () => ({
  currentSection: 1,
  values: { influencers: [] },
  errors: {},
  // True once the user edits anything on Step 2 — only then does leaving ask first.
  requireExitConfirmation: false,
});

// Every user edit on Step 2 goes through one of the confirm reducers below.
const markConfirmEdited = (state) => {
  state.confirm.requireExitConfirmation = true;
};

const initialState = {
  // Wizard state machine: 'form' | 'processing' | 'success' | 'confirm' | 'failure' | 'submitted'
  phase: 'form',
  runAudit: emptyRunAudit(),
  // `awaitingIngest` gates the IngestEchoListener: it only watches/routes AFTER the
  // create/add-files request resolves, so a re-upload can't read the stale pre-upload
  // status and bounce the wizard back mid-upload. `ingestMessage` holds the latest
  // channel message (e.g. "retrying (attempt N of 3)") so the processing screen can
  // surface it over the cosmetic rotation.
  processing: { fileStatuses: {}, awaitingIngest: false, ingestMessage: null },
  // The created campaign-audit (create response), once processing starts.
  audit: null,
  // Step 2 "Confirm" form state.
  confirm: emptyConfirm(),
  // True while GET /{id}/summary is loading the confirm prefill (ML call, 3–5s).
  confirmLoading: false,
  // "Discard your progress?" exit guard
  // variant: 'discard' (leave Step 1 / processing) | 'review' ("Exit Review" from Step 2).
  exitModal: { open: false, pendingPath: null, variant: 'discard' },
  submitError: null,

  // Audits homepage (list) — populated from `listCampaignAudits` on mount.
  audits: [],
  auditsLoading: false,

  // Audit Report screen (/audits/:id/report). `reportAudit` holds the mapped detail
  // (header card fields + adherence score); `report` holds the raw report payload.
  report: null,
  reportAudit: null,
  reportLoading: false,
  reportError: null,
};

const AuditsSlice = createSlice({
  name: 'Audits',
  initialState,
  reducers: {
    setPhase(state, action) {
      state.phase = action.payload;
    },
    setAwaitingIngest(state, action) {
      state.processing.awaitingIngest = action.payload;
    },
    setIngestMessage(state, action) {
      state.processing.ingestMessage = action.payload || null;
    },
    setUploadedDocuments(state, action) {
      state.runAudit.uploadedDocuments = action.payload;
    },
    removeUploadedDocument(state, action) {
      state.runAudit.uploadedDocuments = state.runAudit.uploadedDocuments.filter(
        (doc) => doc.uid !== action.payload
      );
    },
    setCampaignNotes(state, action) {
      state.runAudit.campaignNotes = action.payload;
    },
    // The UI keeps files and a campaign exclusive (the picker is disabled while files
    // are attached; the upload is disabled while a campaign is selected).
    setAuditCampaignId(state, action) {
      state.runAudit.campaignId = action.payload ?? null;
    },
    initFileStatuses(state, action) {
      // payload: array of uids
      state.processing.fileStatuses = action.payload.reduce((acc, uid) => {
        acc[uid] = 'pending';
        return acc;
      }, {});
    },
    setFileStatus(state, action) {
      const { uid, status } = action.payload;
      state.processing.fileStatuses[uid] = status;
    },
    setAudit(state, action) {
      state.audit = action.payload;
    },
    initConfirm(state, action) {
      const prefill = action.payload || {};
      state.confirm = {
        currentSection: 1,
        values: { influencers: [], ...prefill },
        errors: {},
        requireExitConfirmation: false,
      };
    },
    setConfirmSection(state, action) {
      state.confirm.currentSection = action.payload;
    },
    updateConfirmValues(state, action) {
      markConfirmEdited(state);
      state.confirm.values = { ...state.confirm.values, ...action.payload };
      // Clear-on-edit: drop errors for the fields being changed.
      Object.keys(action.payload || {}).forEach((key) => {
        delete state.confirm.errors[key];
      });
    },
    setConfirmErrors(state, action) {
      state.confirm.errors = action.payload || {};
    },
    clearConfirmErrors(state) {
      state.confirm.errors = {};
    },
    addInfluencer(state, action) {
      markConfirmEdited(state);
      state.confirm.values.influencers = [
        ...(state.confirm.values.influencers || []),
        action.payload,
      ];
    },
    updateInfluencer(state, action) {
      markConfirmEdited(state);
      const { id, ...patch } = action.payload;
      state.confirm.values.influencers = (state.confirm.values.influencers || []).map((inf) =>
        inf.id === id
          ? { ...inf, ...patch, outcomes: { ...inf.outcomes, ...(patch.outcomes || {}) } }
          : inf
      );
    },
    removeInfluencer(state, action) {
      markConfirmEdited(state);
      state.confirm.values.influencers = (state.confirm.values.influencers || []).filter(
        (inf) => inf.id !== action.payload
      );
    },
    setSubmitError(state, action) {
      state.submitError = action.payload;
    },
    setConfirmLoading(state, action) {
      state.confirmLoading = action.payload;
    },
    setAudits(state, action) {
      state.audits = action.payload || [];
    },
    setAuditsLoading(state, action) {
      state.auditsLoading = action.payload;
    },
    setReport(state, action) {
      state.report = action.payload;
    },
    setReportAudit(state, action) {
      state.reportAudit = action.payload;
    },
    setReportLoading(state, action) {
      state.reportLoading = action.payload;
    },
    setReportError(state, action) {
      state.reportError = action.payload;
    },
    resetReport(state) {
      state.report = null;
      state.reportAudit = null;
      state.reportLoading = false;
      state.reportError = null;
    },
    removeAudit(state, action) {
      state.audits = state.audits.filter((audit) => audit.id !== action.payload);
    },
    updateAuditRow(state, action) {
      const { id, card, ...patch } = action.payload;
      state.audits = state.audits.map((audit) =>
        audit.id === id
          ? { ...audit, ...patch, card: card ? { ...audit.card, ...card } : audit.card }
          : audit
      );
    },
    // Payload: the blocked path (string), or `{ path, variant }`.
    openExitModal(state, action) {
      const { payload } = action;
      const isObject = payload != null && typeof payload === 'object';
      state.exitModal.open = true;
      state.exitModal.pendingPath = (isObject ? payload.path : payload) || null;
      state.exitModal.variant = (isObject && payload.variant) || 'discard';
    },
    closeExitModal(state) {
      state.exitModal.open = false;
      state.exitModal.pendingPath = null;
      state.exitModal.variant = 'discard';
    },
    resetRunAudit(state) {
      state.phase = 'form';
      state.runAudit = emptyRunAudit();
      state.processing = { fileStatuses: {}, awaitingIngest: false, ingestMessage: null };
      state.audit = null;
      state.confirm = emptyConfirm();
      state.submitError = null;
    },
  },
});

export const {
  setPhase,
  setAwaitingIngest,
  setIngestMessage,
  setUploadedDocuments,
  removeUploadedDocument,
  setCampaignNotes,
  setAuditCampaignId,
  initFileStatuses,
  setFileStatus,
  setAudit,
  initConfirm,
  setConfirmSection,
  updateConfirmValues,
  setConfirmErrors,
  clearConfirmErrors,
  addInfluencer,
  updateInfluencer,
  removeInfluencer,
  setSubmitError,
  setConfirmLoading,
  setAudits,
  setAuditsLoading,
  setReport,
  setReportAudit,
  setReportLoading,
  setReportError,
  resetReport,
  removeAudit,
  updateAuditRow,
  openExitModal,
  closeExitModal,
  resetRunAudit,
} = AuditsSlice.actions;

// Where a `needs_info` audit asks for the missing info: an upload audit goes back to
// Step 1 (banner + re-upload); a campaign audit can't take files, so its missing info
// (e.g. the creators it used) is answered in Step 2 — Section 4 adds the influencers.
const needsInfoPhase = (audit, fromCampaign = false) =>
  fromCampaign || isCampaignAudit(audit) ? 'confirm' : 'form';

// Route the processing screen once the ML has SETTLED on a status. Returns true
// when it routed; false for the in-flight `pending`/`processing` states (keep waiting).
const routeSettledStatus = (dispatch, getState) => {
  const { audit, runAudit } = getState().Audits;
  const status = audit?.status;
  if (status === AUDIT_STATUS.NEEDS_INFO) {
    dispatch(setPhase(needsInfoPhase(audit, runAudit.campaignId != null)));
    return true;
  }
  if (status === AUDIT_STATUS.FAILED) {
    dispatch(setPhase('failure'));
    return true;
  }
  if (
    status === AUDIT_STATUS.READY_FOR_CONFIRMATION ||
    status === AUDIT_STATUS.CONFIRMED ||
    status === AUDIT_STATUS.ANALYZED
  ) {
    dispatch(setPhase('success'));
    return true;
  }
  return false;
};

/**
 * Start (or retry) audit ingest against the real API.
 *
 * First submit → `createCampaignAudit`; a re-upload while `needs_info` →
 * `addCampaignAuditFiles`. Per contract v2 these return **immediately** with
 * `status: pending`; the ML ingests in the background (~10–15s) and the answer
 * arrives via the `campaign-audit.{id}` `ingest` events (IngestEchoListener) or a
 * `GET /{id}` poll. So we only route here if the BE already settled; otherwise we
 * stay on the processing screen and let `applyIngestUpdate` route.
 */
// In-flight guards: one create/add-files and one confirm+run at a time, so a fast
// double click can never send the same write twice (which would duplicate the audit).
let processingInFlight = false;
let submitInFlight = false;

export const startAuditProcessing = () => (dispatch, getState) => {
  if (processingInFlight) return Promise.resolve();
  const { runAudit, audit } = getState().Audits;
  const files = runAudit.uploadedDocuments;
  const isReupload = audit?.id != null && audit.status === AUDIT_STATUS.NEEDS_INFO;
  // An audit already exists past ingest: never create a second one for the same run.
  if (audit?.id != null && !isReupload) return Promise.resolve();

  dispatch(setSubmitError(null));
  dispatch(setIngestMessage(null));
  // Don't let the ingest watcher poll/route until this upload request has resolved —
  // otherwise a re-upload reads the stale pre-upload status and bounces mid-upload.
  dispatch(setAwaitingIngest(false));
  dispatch(setPhase('processing'));

  // Re-ingest (upload audits only — campaign audits answer needs_info in Step 2). New
  // audit: start from the picked campaign (`campaignId`, no files) or the uploaded files.
  let request;
  if (isReupload) {
    request = addCampaignAuditFiles(audit.id, { files, text: runAudit.campaignNotes });
  } else if (runAudit.campaignId != null) {
    request = createCampaignAudit({
      campaignId: runAudit.campaignId,
      text: runAudit.campaignNotes,
    });
  } else {
    request = createCampaignAudit({ files, text: runAudit.campaignNotes, campaignIds: [] });
  }

  processingInFlight = true;
  return request
    .then((resp) => {
      if (!isApiSuccess(resp)) {
        dispatch(
          setSubmitError(resp?.error || resp?.message || 'Upload failed. Please try again.')
        );
        dispatch(setPhase('failure'));
        return;
      }
      const data = resp.data || {};
      dispatch(setAudit(data));
      // pending/processing → keep waiting; the ingest listener/poll routes when it settles
      // (only now, post-resolve, do we start watching). The per-file animation is cosmetic.
      const routed = routeSettledStatus(dispatch, getState);
      if (!routed) dispatch(setAwaitingIngest(true));
    })
    .catch(() => {
      dispatch(setSubmitError('Upload failed. Please try again.'));
      dispatch(setPhase('failure'));
    })
    .finally(() => {
      processingInFlight = false;
    });
};

/**
 * Apply an ingest update (a Reverb `.status.updated` payload, or a `GET /{id}`
 * poll response's audit object) while the processing screen is showing: merge the
 * latest audit fields and route once the status settles. No-op if we've already
 * left the processing screen.
 */
export const applyIngestUpdate = (data) => (dispatch, getState) => {
  const { phase, processing } = getState().Audits;
  // Only act while showing the processing screen AND after the upload resolved.
  if (!data || phase !== 'processing' || !processing.awaitingIngest) return;
  const current = getState().Audits.audit || {};
  dispatch(
    setAudit({
      ...current,
      status: data.status ?? current.status,
      isAnalyzable: data.isAnalyzable ?? current.isAnalyzable,
      completeness: data.completeness ?? current.completeness,
      errorMessage: data.errorMessage ?? current.errorMessage,
      sources: data.sources ?? current.sources,
    })
  );
  // Surface the channel message (e.g. "retrying (attempt N of 3)") for the processing screen.
  if (data.message !== undefined) dispatch(setIngestMessage(data.message));
  if (routeSettledStatus(dispatch, getState)) dispatch(setAwaitingIngest(false));
};

/**
 * Seed the confirm form from the live ML summary (GET /{id}/summary, ~3–5s).
 * Called when the confirm screen mounts.
 */
export const loadConfirmFromSummary = () => (dispatch, getState) => {
  const { audit } = getState().Audits;
  if (!audit?.id) return Promise.resolve();
  dispatch(setConfirmLoading(true));
  return getCampaignAuditSummary(audit.id)
    .then((resp) => {
      if (isApiSuccess(resp) && resp.data?.summary) {
        dispatch(initConfirm(mapSummaryToConfirm(resp.data.summary)));
      }
    })
    .catch(() => {})
    .finally(() => dispatch(setConfirmLoading(false)));
};

// Maps the confirm-form values to the POST /{id}/confirmations body (doc shape).
// Spend values are sent as raw numbers ("numbers with no symbols").
// "48,000" → 48000; empty / non-numeric → null.
const toNumberOrNull = (value) => {
  const digits = stripCommas(value);
  return digits ? Number(digits) : null;
};

// Profile URLs are stored without a scheme (the input shows an https:// prefix).
const withScheme = (url) => (/^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? url : `https://${url}`);

// FE outcome inputs → the ML's outcome keys (inverse of mapSummaryOutcomes): the
// first input goes back as `impressions` or `views` (whichever the ML reported) and
// engagements as `engagement`. Empty inputs are omitted.
const toSummaryOutcomes = (inf = {}) => {
  const outcomes = inf.outcomes || {};
  const body = {
    [inf.viewsKey === 'impressions' ? 'impressions' : 'views']: toNumberOrNull(outcomes.views),
    clicks: toNumberOrNull(outcomes.clicks),
    conversions: toNumberOrNull(outcomes.conversions),
    engagement: toNumberOrNull(outcomes.engagements),
  };
  return Object.fromEntries(Object.entries(body).filter(([, value]) => value != null));
};

const mapConfirmToCorrections = (values = {}) => {
  const body = {
    subjectType: values.subjectType === 'other' ? values.otherSubjectType : values.subjectType,
    subjectName: values.subjectCompany,
    subjectUrl: values.productUrl,
    subjectSummary: values.subjectSummary,
    objective: values.objective,
    currency: values.currency,
    // The free-text timeframe note isn't collected any more; the ML keeps its own.
    timeframe: {
      start: values.campaignStartDate,
      end: values.campaignEndDate,
    },
  };

  // Contract v7: `totals: { totalSpend, currency }` — omitted when no spend was given.
  const totalSpend = toNumberOrNull(values.totalSpend);
  if (totalSpend != null) body.totals = { totalSpend, currency: values.currency };

  // v2: never send `influencers: []` — an empty list clears the roster and drops the
  // audit back to needs_info. Only include it when we actually have influencers, and
  // send the contract fields (not the FE-only `id` / `profileUrl`). Influencer details
  // are optional, so empty ones are left out rather than sent as null. That includes
  // `outcomes`: an empty `{}` is decoded by the API as an empty PHP array and reaches
  // the ML as `[]` → "Input should be a valid dictionary".
  const influencers = (values.influencers || []).map((inf) => {
    const spend = toNumberOrNull(inf.spend);
    const posts = toNumberOrNull(inf.posts);
    const outcomes = toSummaryOutcomes(inf);
    return {
      ...(inf.handle ? { handle: inf.handle } : {}),
      ...(inf.platform ? { platform: inf.platform } : {}),
      ...(inf.profileUrl ? { url: withScheme(inf.profileUrl) } : {}),
      ...(spend != null ? { spend, spendCurrency: inf.spendCurrency } : {}),
      ...(posts != null ? { posts } : {}),
      ...(Object.keys(outcomes).length ? { outcomes } : {}),
    };
  });
  if (influencers.length) body.influencers = influencers;
  return body;
};

// First readable error from a failed response — prefers the ML's field errors
// (e.g. `influencers.0.posts: Input should be a valid list`), else error/message.
const firstReadableError = (resp) => {
  const errors = resp?.errors;
  if (errors && typeof errors === 'object') {
    const key = Object.keys(errors)[0];
    const val = key ? errors[key] : null;
    const msg = Array.isArray(val) ? val[0] : val;
    if (msg) return `${key}: ${msg}`;
  }
  return resp?.error || resp?.message || null;
};

/**
 * Recover a read-failed audit: DELETE each refused/unreadable source, then
 * POST /{id}/sources/retry to re-queue the rest. Mirrors `startAuditProcessing` —
 * returns to the processing screen and lets the IngestEchoListener route once the
 * re-ingest settles. Resolves { success, error }.
 */
export const retryIngestSources = () => (dispatch, getState) => {
  const { audit } = getState().Audits;
  if (!audit?.id) return Promise.resolve({ success: false });

  const removable = (audit.sources || []).filter(isRemovableSource);

  dispatch(setSubmitError(null));
  dispatch(setIngestMessage(null));
  dispatch(setAwaitingIngest(false));
  dispatch(setPhase('processing'));

  const removals = removable
    .filter((source) => source.id != null)
    .map((source) => deleteCampaignAuditSource(audit.id, source.id));

  return Promise.all(removals)
    .then(() => retryCampaignAuditSources(audit.id))
    .then((resp) => {
      if (!isApiSuccess(resp)) {
        const error = firstReadableError(resp) || 'Retry failed. Please try again.';
        dispatch(setSubmitError(error));
        dispatch(setPhase('failure'));
        return { success: false, error };
      }
      const data = resp.data || {};
      dispatch(setAudit(data));
      const routed = routeSettledStatus(dispatch, getState);
      if (!routed) dispatch(setAwaitingIngest(true));
      return { success: true };
    })
    .catch(() => {
      dispatch(setSubmitError('Retry failed. Please try again.'));
      dispatch(setPhase('failure'));
      return { success: false };
    });
};

/**
 * Submit the confirmed audit: POST /{id}/confirmations then POST /{id}/runs.
 * Resolves { success } so the caller can toast and navigate to the homepage
 * (where the queued run resolves live over Reverb).
 */
export const submitConfirmedAudit = () => (dispatch, getState) => {
  // A second submit while the first is in flight is ignored (no toast, no re-send).
  if (submitInFlight) return Promise.resolve({ success: false, ignored: true });
  const { audit, confirm } = getState().Audits;
  if (!audit?.id) return Promise.resolve({ success: false });

  // Plain-language error + the Step 2 section holding the bad field (to jump there).
  const failure = (resp) => {
    const { message, section } = friendlyConfirmError(resp, confirm.values);
    return { success: false, error: message, section };
  };

  submitInFlight = true;
  return confirmCampaignAudit(audit.id, mapConfirmToCorrections(confirm.values))
    .then((resp) => {
      if (!isApiSuccess(resp)) return failure(resp);
      if (resp.data) dispatch(setAudit(resp.data));
      return runCampaignAudit(audit.id).then((runResp) => {
        if (!isApiSuccess(runResp)) return failure(runResp);
        if (runResp.data) dispatch(setAudit(runResp.data));
        return { success: true };
      });
    })
    .catch(() => ({ success: false }))
    .finally(() => {
      submitInFlight = false;
    });
};

/** Return to the form and clear the run (Back to Step 1 / Discard changes). */
export const cancelAudit = () => (dispatch) => {
  dispatch(resetRunAudit());
};

/**
 * Resume a `needs_info` audit in the wizard: fetch the full audit (for
 * `completeness.missing`), reset the run, and load it where the missing info is
 * asked — Step 1 (banner + re-upload) for upload audits, Step 2 for campaign audits.
 * The caller navigates to `/audits/run`. Resolves { success }.
 */
export const resumeNeedsInfo = (id) => (dispatch) =>
  getCampaignAudit(id)
    .then((resp) => {
      if (!isApiSuccess(resp) || !resp.data) return { success: false };
      dispatch(resetRunAudit());
      dispatch(setAudit(resp.data));
      dispatch(setPhase(needsInfoPhase(resp.data)));
      return { success: true };
    })
    .catch(() => ({ success: false }));

/**
 * Open a `ready_for_confirmation` audit straight on the confirm step: fetch the
 * full audit, reset the run, load it at the confirm phase, and let the caller
 * navigate to `/audits/run`. The confirm screen seeds itself from GET /{id}/summary
 * on mount (`loadConfirmFromSummary`). Resolves { success }.
 */
export const resumeConfirmation = (id) => (dispatch) =>
  getCampaignAudit(id)
    .then((resp) => {
      if (!isApiSuccess(resp) || !resp.data) return { success: false };
      dispatch(resetRunAudit());
      dispatch(setAudit(resp.data));
      dispatch(setPhase('confirm'));
      return { success: true };
    })
    .catch(() => ({ success: false }));

/**
 * Load the Audits homepage list and map rows to card view-models. Pass
 * `status: 'archived'` to view archived audits; any other (or no) status loads the
 * active list and strips archived rows client-side (robust whether or not the BE
 * includes archived in the default list).
 */
export const fetchAuditsList = (status) => (dispatch) => {
  dispatch(setAuditsLoading(true));
  return listCampaignAudits({ status, sort: 'createdAt', order: 'DESC' })
    .then((resp) => {
      if (!isApiSuccess(resp)) {
        dispatch(setAudits([]));
        return;
      }
      const rows = resp.data?.requests || [];
      const visible =
        status === AUDIT_STATUS.ARCHIVED
          ? rows
          : rows.filter((row) => row.status !== AUDIT_STATUS.ARCHIVED);
      dispatch(setAudits(visible.map(mapListRowToCard)));
    })
    .catch(() => dispatch(setAudits([])))
    .finally(() => dispatch(setAuditsLoading(false)));
};

/**
 * Load the Audit Report screen: fetch the audit detail (header card fields +
 * adherence score, known shape) and the report payload (narrative + future
 * sections) in parallel. The header still renders if the report 404s; only a
 * failed detail is treated as an error.
 */
export const fetchAuditReport = (id) => (dispatch) => {
  dispatch(setReportLoading(true));
  dispatch(setReportError(null));
  return Promise.all([getCampaignAudit(id), getCampaignAuditReport(id)])
    .then(([detailResp, reportResp]) => {
      if (isApiSuccess(detailResp) && detailResp.data) {
        dispatch(setReportAudit(buildCardView(detailResp.data)));
      } else {
        dispatch(setReportError(firstReadableError(detailResp) || 'Report unavailable.'));
      }
      // The endpoint nests the content under data.report ({ id, status, version, generatedAt, report }).
      dispatch(setReport(isApiSuccess(reportResp) ? reportResp.data?.report || null : null));
    })
    .catch(() => dispatch(setReportError('Report unavailable.')))
    .finally(() => dispatch(setReportLoading(false)));
};

/**
 * Re-fetch one audit (catch-up on socket reconnect / terminal status) and patch its
 * row — including the refreshed `card` so the adherence score appears once a run
 * completes (the detail carries the same `card`/`createdBy` as the list rows).
 */
export const refreshAudit = (id) => (dispatch) =>
  getCampaignAudit(id)
    .then((resp) => {
      if (isApiSuccess(resp) && resp.data) {
        dispatch(
          updateAuditRow({
            id,
            status: resp.data.status,
            isAnalyzable: resp.data.isAnalyzable,
            card: buildCardView(resp.data),
          })
        );
      }
    })
    .catch(() => {});

// A DELETE can succeed with a JSON envelope, a `{ deleted: true }` body, or an
// empty 2xx (No Content) — accept all three so the row is actually removed.
const isDeleteSuccess = (resp) =>
  isApiSuccess(resp) ||
  resp?.data?.deleted === true ||
  (resp?.httpStatus >= 200 && resp?.httpStatus < 300);

/** Delete an audit then drop it from the visible list. Resolves { success, error }. */
export const deleteAudit = (id) => (dispatch) =>
  deleteCampaignAudit(id)
    .then((resp) => {
      if (isDeleteSuccess(resp)) {
        dispatch(removeAudit(id));
        return { success: true };
      }
      return { success: false, error: firstReadableError(resp) };
    })
    .catch(() => ({ success: false }));

/** Archive an audit then drop it from the visible list. Resolves { success, error }. */
export const archiveAudit = (id) => (dispatch) =>
  archiveCampaignAudit(id)
    .then((resp) => {
      if (isApiSuccess(resp)) {
        dispatch(removeAudit(id));
        return { success: true };
      }
      return { success: false, error: firstReadableError(resp) };
    })
    .catch(() => ({ success: false }));

/**
 * Restore an archived audit (POST /{id}/unarchive) then drop it from the visible
 * archived list. Resolves { success, error }.
 */
export const unarchiveAudit = (id) => (dispatch) =>
  unarchiveCampaignAudit(id)
    .then((resp) => {
      if (isApiSuccess(resp)) {
        dispatch(removeAudit(id));
        return { success: true };
      }
      return { success: false, error: firstReadableError(resp) };
    })
    .catch(() => ({ success: false }));

/**
 * Rename an audit (PATCH /{id}/rename) then refresh the row from the canonical
 * detail so the card reflects the BE state. Resolves { success, error }. Note: the
 * card title prefers the extracted `subject.name`, so a rename visibly updates
 * pre-analysis (needs_info) titles but not analyzed cards' subject-named titles.
 */
export const renameAudit = (id, name) => (dispatch) =>
  renameCampaignAudit(id, name)
    .then((resp) => {
      if (!isApiSuccess(resp)) return { success: false, error: firstReadableError(resp) };
      dispatch(refreshAudit(id));
      return { success: true };
    })
    .catch(() => ({ success: false }));

/**
 * Confirm a ready-for-confirmation audit as-is (POST /{id}/confirmations {}) then
 * queue the run (POST /{id}/runs). Patches the row to `processing` so it flips to
 * GENERATING and resolves live over Reverb. Resolves { success, error }.
 */
export const confirmAndRunAudit = (id) => (dispatch) =>
  confirmCampaignAudit(id, {})
    .then((resp) => {
      if (!isApiSuccess(resp)) return { success: false, error: firstReadableError(resp) };
      return runCampaignAudit(id).then((runResp) => {
        if (!isApiSuccess(runResp)) return { success: false, error: firstReadableError(runResp) };
        dispatch(updateAuditRow({ id, status: runResp.data?.status || AUDIT_STATUS.PROCESSING }));
        return { success: true };
      });
    })
    .catch(() => ({ success: false }));

/**
 * (Re-)run analysis for a confirmed/failed audit (POST /{id}/runs). Patches the row
 * to `processing`. Resolves { success, error }. Seam: swap for a dedicated re-run
 * endpoint when the BE provides one.
 */
export const rerunAudit = (id) => (dispatch) =>
  runCampaignAudit(id)
    .then((resp) => {
      if (!isApiSuccess(resp)) return { success: false, error: firstReadableError(resp) };
      dispatch(updateAuditRow({ id, status: resp.data?.status || AUDIT_STATUS.PROCESSING }));
      return { success: true };
    })
    .catch(() => ({ success: false }));

/**
 * Re-queue a homepage card whose file read never completed (stuck in `pending`):
 * POST /{id}/sources/retry, then restart the stuck-pending clock from now so the
 * card goes back to "reading files" while the live listener waits for the settle.
 * Resolves { success, error }.
 */
export const retryPendingAudit = (id) => (dispatch) =>
  retryCampaignAuditSources(id)
    .then((resp) => {
      if (!isApiSuccess(resp)) return { success: false, error: firstReadableError(resp) };
      const status = resp.data?.status || AUDIT_STATUS.PENDING;
      dispatch(updateAuditRow({ id, status, pendingSince: Date.now() }));
      // Already settled (no ingest wait) → pull the extracted card fields now.
      if (status !== AUDIT_STATUS.PENDING && status !== AUDIT_STATUS.PROCESSING) {
        dispatch(refreshAudit(id));
      }
      return { success: true };
    })
    .catch(() => ({ success: false }));

export default AuditsSlice.reducer;
